/**
 * Test generation, grading, submission and history.
 *
 * Two of these routes stream. Prefer the streaming twins in any UI a student waits
 * on: `/generate` returns nothing until the whole test exists, while
 * `/generate/stream` hands over each question the moment it is written — including
 * the reusable bank questions, which arrive immediately.
 */

import type { ApiClient } from '../core/client'
import type { QuestionStreamFrame } from '../core/sse'
import { streamFrames } from '../core/sse'
import type { UUID } from '../types/common'
import type {
  GradeTestRequest,
  GradingResult,
  HistoryDetailResponse,
  HistoryListResponse,
  Question,
  QuestionReportRequest,
  QuestionReportResponse,
  RemarkResponse,
  QuickReviseSession,
  QuickReviseSourceSummary,
  QuickReviseStartRequest,
  QuickReviseSubmitRequest,
  TestGenerationRequest,
  TestPlan,
  TestPlanRequest,
  TestResult,
  TestResultCreate,
} from '../types/tests'

/** How well a topic is currently retained, recomputed for *now* rather than read
 *  from the column — which is only R at the last review and would read ≈1.0 forever. */
export interface Retrievability {
  retrievability: number | null
  due_date: string | null
  not_studied: boolean
}

export type TestQuestionFrame = QuestionStreamFrame<Question, QuickReviseSourceSummary>

export class TestsResource {
  constructor(private readonly client: ApiClient) {}

  // -- generation ------------------------------------------------------------

  generate(body: TestGenerationRequest, signal?: AbortSignal): Promise<Question[]> {
    return this.client.post<Question[]>('/tests/generate', body, signal ? { signal } : {})
  }

  /**
   * Previews the server-selected mix, marks and duration. It is safe to request
   * whenever the student changes the question count: it neither generates a
   * question nor writes an attempt.
   */
  plan(body: TestPlanRequest, signal?: AbortSignal): Promise<TestPlan> {
    return this.client.post<TestPlan>('/tests/plan', body, signal ? { signal } : {})
  }

  /**
   * Yields bank questions first (`origin: 'bank'`), then each generated question as
   * the model finishes writing it, then a terminal `done` frame.
   *
   * Over-budget behaviour differs from the batch route on purpose: rather than a
   * hard 429 that throws away a partial bank hit, this streams whatever the bank
   * had. Only when there is nothing at all to hand back does it 429.
   */
  generateStream(
    body: TestGenerationRequest,
    signal?: AbortSignal,
  ): AsyncGenerator<TestQuestionFrame, void, undefined> {
    return streamFrames<TestQuestionFrame>(
      this.client,
      '/tests/generate/stream',
      body,
      signal ? { signal } : {},
    )
  }

  // -- quick revise ----------------------------------------------------------

  startQuickRevise(
    body: QuickReviseStartRequest = {},
    signal?: AbortSignal,
  ): Promise<QuickReviseSession> {
    return this.client.post<QuickReviseSession>(
      '/tests/quick-revise/start',
      body,
      signal ? { signal } : {},
    )
  }

  /** The first frame carries `session_id` and `source_summary`; questions follow. */
  startQuickReviseStream(
    body: QuickReviseStartRequest = {},
    signal?: AbortSignal,
  ): AsyncGenerator<TestQuestionFrame, void, undefined> {
    return streamFrames<TestQuestionFrame>(
      this.client,
      '/tests/quick-revise/start/stream',
      body,
      signal ? { signal } : {},
    )
  }

  submitQuickRevise(sessionId: UUID, body: QuickReviseSubmitRequest): Promise<unknown> {
    return this.client.post<unknown>(`/tests/quick-revise/${sessionId}/submit`, body)
  }

  // -- grading and submission ------------------------------------------------

  /**
   * Grades written and calculation answers. Multiple choice is settled server-side
   * at submit and does not need this.
   *
   * A result with `grading_unavailable` is not a zero — it means grading could not
   * run. Rendering it as a score is the worst thing the results screen can do.
   */
  grade(body: GradeTestRequest, signal?: AbortSignal): Promise<GradingResult[]> {
    return this.client.post<GradingResult[]>('/tests/grade', body, signal ? { signal } : {})
  }

  submit(body: TestResultCreate): Promise<TestResult> {
    return this.client.post<TestResult>('/tests/submit', body)
  }

  // -- history ---------------------------------------------------------------

  /** A slim list. Question snapshots are not included — fetch `historyDetail` when
   *  an attempt is actually opened. */
  history(
    params?: { limit?: number; cursor?: string },
    signal?: AbortSignal,
  ): Promise<HistoryListResponse> {
    return this.client.get<HistoryListResponse>('/tests/history', params, signal)
  }

  historyDetail(attemptId: UUID, signal?: AbortSignal): Promise<HistoryDetailResponse> {
    return this.client.get<HistoryDetailResponse>(`/tests/history/${attemptId}`, undefined, signal)
  }

  deleteResult(resultId: UUID): Promise<void> {
    return this.client.delete<void>(`/tests/${resultId}`)
  }

  /** Report content that is wrong or unsuitable. The server may withdraw it from
   * future attempts immediately; the reporter's existing marks do not change. */
  reportQuestion(questionId: UUID, body: QuestionReportRequest): Promise<QuestionReportResponse> {
    return this.client.post<QuestionReportResponse>(`/questions/${questionId}/report`, body)
  }

  /** One independent second opinion for a completed answer. */
  remarkQuestion(attemptId: UUID, questionId: UUID): Promise<RemarkResponse> {
    return this.client.post<RemarkResponse>(`/attempts/${attemptId}/questions/${questionId}/remark`)
  }

  retrievability(topicId: UUID, signal?: AbortSignal): Promise<Retrievability> {
    return this.client.get<Retrievability>('/tests/fsrs/retrievability', { topic_id: topicId }, signal)
  }
}
